// Snapping helpers for the Konva timeline.
//
// Phase 5 fills in the full snap-to-markers / snap-to-region-edges logic. For
// now we expose a stable API so editing code can import.

/**
 * @typedef {Object} SnapInput
 * @property {number} time          - candidate time in seconds
 * @property {number[]} [markers]   - marker times (seconds) to snap against
 * @property {number[]} [edges]     - other event start/end times (seconds)
 * @property {number} pxPerSec
 * @property {number} [frameGap]    - minimum gap (seconds) that must remain
 * @property {boolean} [snapMarkers]
 * @property {boolean} [snapEdges]
 * @property {number} [tolerancePx] - default 6
 */

/**
 * Snap a candidate time to the nearest marker or event edge if it lies within
 * `tolerancePx` of one.
 *
 * @param {SnapInput} input
 * @returns {{ time: number, snapped: false | { type: 'marker'|'edge', target: number } }}
 */
export function snapTime({
    time,
    markers,
    edges,
    pxPerSec,
    snapMarkers = false,
    snapEdges = false,
    tolerancePx = 6
}) {
    if (!Number.isFinite(time) || !pxPerSec) return { time, snapped: false };
    const tol = tolerancePx / pxPerSec;
    let best = null;

    if (snapMarkers && Array.isArray(markers)) {
        for (let i = 0; i < markers.length; i += 1) {
            const t = markers[i];
            if (!Number.isFinite(t)) continue;
            const d = Math.abs(t - time);
            if (d <= tol && (!best || d < best.distance)) {
                best = { distance: d, type: "marker", target: t };
            }
        }
    }

    if (snapEdges && Array.isArray(edges)) {
        for (let i = 0; i < edges.length; i += 1) {
            const t = edges[i];
            if (!Number.isFinite(t)) continue;
            const d = Math.abs(t - time);
            if (d <= tol && (!best || d < best.distance)) {
                best = { distance: d, type: "edge", target: t };
            }
        }
    }

    if (!best) return { time, snapped: false };
    return {
        time: best.target,
        snapped: { type: best.type, target: best.target }
    };
}

/**
 * Convert a frame-gap (in frames) and a frame rate to seconds.
 * @param {number} frames
 * @param {number} frameRate
 * @returns {number}
 */
export function frameGapToSeconds(frames, frameRate) {
    if (!frames || !frameRate) return 0;
    return frames / frameRate;
}
